import { Component, Input, Output, EventEmitter, signal, computed, inject, OnInit, AfterViewInit, OnChanges, SimpleChanges, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
// PrimeNG 21 Imports
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { Table } from 'primeng/table';
import { ColumnConfig } from '../../backoffice/components/dashboard/table-config';

/**
 * Componente generico riutilizzabile per la gestione di tabelle di registro
 * Gestisce: filtri, ordinamento, paginazione, azioni, toast notifications
 */
@Component({
  selector: 'app-registry-table',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    NgTemplateOutlet,
    FormsModule,
    ButtonModule,
    TagModule,
    TableModule,
    InputTextModule,
    DatePickerModule,
    SelectModule,
    MultiSelectModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    Toast,
  ],
  templateUrl: './registry-table.component.html',
  styleUrls: ['./registry-table.component.scss'],
})
export class RegistryTableComponent implements OnInit, AfterViewInit, OnChanges {
  private messageService = inject(MessageService);
  private readonly dateTimeFormatter = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  // Input properties
  @Input() title: string = 'Registro';
  @Input() icon: string = 'pi-list';
  @Input() columns: ColumnConfig[] = [];
  @Input() data: any[] = [];
  @Input() loading: boolean = false;
  @Input() showClearFiltersButton: boolean = true;
  /**
   * Mostra il pulsante "Esporta". Default false (opt-in): va attivato
   * esplicitamente solo dai registri che hanno un handler onExport funzionante,
   * così il pulsante non compare sulle tabelle che non supportano l'export.
   */
  @Input() showExportButton: boolean = false;
  @Input() emptyStateTitle: string = 'Nessun elemento registrato';
  @Input() emptyStateMessage: string = 'Non ci sono elementi da visualizzare in questo momento.';
  @Input() externalFiltersActive: boolean = false;
  @Input() lockedFilterFields: string[] = [];
  @Input() ignoreStatusChecks: boolean = false;
  @Input() deleteExcludedStatuses: string[] = [];
  @Input() customActionsTemplate: TemplateRef<any> | null = null;
  /** Restituisce una classe CSS opzionale per la riga (es. evidenziazione). */
  @Input() rowStyleClass: ((rowData: any) => string) | null = null;
  /** Filtri pre-impostati: { fieldName: value } — applicati visivamente al caricamento */
  @Input() initialFilters: Record<string, any> = {};
  /**
   * Se true, i filtri NON vengono applicati localmente da PrimeNG:
   * ogni cambio filtro emette solo onFilterChange e il parent chiama il BE.
   * Se false (default), il filtro è gestito internamente dalla p-table.
   */
  @Input() serverSideFilter: boolean = false;

  @ViewChild('dt') dt!: Table;

  // Flag: initial filters already applied to p-table
  private initialFiltersApplied = false;

  // Output events
  @Output() onDetailClick = new EventEmitter<any>();
  @Output() onDuplicateClick = new EventEmitter<any>();
  @Output() onEditClick = new EventEmitter<any>();
  @Output() onDeleteClick = new EventEmitter<any>();
  @Output() onDownloadClick = new EventEmitter<any>();
  @Output() onSelectClick = new EventEmitter<any>();
  @Output() onClearFilters = new EventEmitter<void>();
  @Output() onExport = new EventEmitter<void>();
  @Output() onFilterChange = new EventEmitter<{ value: any; field: string; matchMode: string }>();

  // Internal state
  filterValues = signal<Record<string, any>>({});
  filterErrors = signal<Record<string, { message: string; suggestion: string } | null>>({});
  originalData = signal<any[]>([]);

  /** True se almeno un filtro ha un valore non vuoto */
  hasActiveFilters = computed(() =>
    Object.values(this.filterValues()).some((v) =>
      v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
    )
  );

  hasAnyActiveFilters(): boolean {
    return this.hasActiveFilters() || this.externalFiltersActive;
  }

  isFilterLocked(field: string): boolean {
    return this.lockedFilterFields.includes(field);
  }

  /** Inoltra al parent la richiesta di export (es. download Excel del registro). */
  handleExport(): void {
    this.onExport.emit();
  }

  formatDateTime(value: Date | string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '-';

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    const parts = Object.fromEntries(
      this.dateTimeFormatter.formatToParts(date).map(part => [part.type, part.value])
    );
    return `${parts['day']}/${parts['month']}/${parts['year']} ${parts['hour']}:${parts['minute']}:${parts['second']}`;
  }

  private buildFilterState(initialFilters: Record<string, any> = this.initialFilters): {
    filters: Record<string, any>;
    errors: Record<string, { message: string; suggestion: string } | null>;
  } {
    const filters: Record<string, any> = {};
    const errors: Record<string, { message: string; suggestion: string } | null> = {};

    this.columns.forEach((col) => {
      if (col.field && col.filterType && col.filterType !== 'none') {
        const initialValue = initialFilters[col.field];

        if (col.filterType === 'multiselect') {
          filters[col.field] = Array.isArray(initialValue)
            ? initialValue
            : initialValue !== null && initialValue !== undefined && initialValue !== ''
              ? [initialValue]
              : [];
        } else if (col.filterType === 'select') {
          filters[col.field] = Array.isArray(initialValue)
            ? (initialValue[0] ?? null)
            : (initialValue ?? null);
        } else {
          filters[col.field] = initialValue ?? (col.filterType === 'text' ? '' : null);
        }

        errors[col.field] = null;
      }
    });

    return { filters, errors };
  }

  private syncFilterValues(initialFilters: Record<string, any> = this.initialFilters): void {
    const { filters, errors } = this.buildFilterState(initialFilters);
    this.filterValues.set(filters);
    this.filterErrors.set(errors);
  }

  ngOnInit(): void {
    this.syncFilterValues();
    this.originalData.set([...this.data]);
  }

  ngAfterViewInit(): void {
    // Attempt to apply on view init (covers case where data is already present)
    this.applyInitialFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFilters']) {
      this.initialFiltersApplied = false;
      this.syncFilterValues(changes['initialFilters'].currentValue || {});

      if (this.dt) {
        this.dt.clear();
        setTimeout(() => this.applyInitialFilters(), 0);
      }
    }

    // Aggiorna lo snapshot dell'ordine originale quando arriva un nuovo array
    // dati dal parent (necessario per ripristinare l'ordine su "Azzera filtri",
    // dato che PrimeNG ordina l'array in place).
    if (changes['data']) {
      const newData = changes['data'].currentValue;
      if (Array.isArray(newData)) {
        this.originalData.set([...newData]);
      }
    }

    // Re-apply when data arrives from API (empty → populated)
    if (changes['data'] && !this.initialFiltersApplied && Object.keys(this.initialFilters).length > 0) {
      const newData = changes['data'].currentValue;
      if (newData && newData.length > 0) {
        setTimeout(() => this.applyInitialFilters(), 0);
      }
    }
  }

  private applyInitialFilters(): void {
    if (this.initialFiltersApplied || !this.dt || Object.keys(this.initialFilters).length === 0) return;
    const hasData = this.data && this.data.length > 0;
    if (!hasData) return; // wait for data
    this.initialFiltersApplied = true;
    if (!this.serverSideFilter) {
      Object.entries(this.initialFilters).forEach(([field, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          const col = this.columns.find(c => c.field === field);
          if (col?.filterType === 'multiselect') {
            this.dt.filter(Array.isArray(value) ? value : [value], field, 'in');
            return;
          }
          const matchMode = col?.filterType === 'text' ? 'contains' : 'equals';
          this.dt.filter(value, field, matchMode);
        }
      });
    }
  }

  // =====================
  // TABLE FILTERS
  // =====================
  clearAllFilters(table: any): void {
    // Reset table filters and sorting
    table.clear();
    table.sortField = null;
    table.sortOrder = 1;
    table._sortOrder = 1;

    // Ripristina l'ordine originale dei dati: PrimeNG ordina l'array in place,
    // quindi table.clear() azzera solo i metadati di sort ma le righe restano
    // nell'ultimo ordinamento applicato. Rimettiamo l'array nell'ordine di
    // partenza (per la tabella client-side; il lato server gestisce il proprio).
    if (!this.serverSideFilter && Array.isArray(this.data) && this.originalData().length > 0) {
      this.data.splice(0, this.data.length, ...this.originalData());
      if (table.value) {
        table.value = this.data;
      }
    }

    // Reset filter values
    const filters: Record<string, any> = {};
    const errors: Record<string, { message: string; suggestion: string } | null> = {};
    this.columns.forEach((col) => {
      if (col.field && col.filterType && col.filterType !== 'none') {
        filters[col.field] = col.filterType === 'text' ? '' : col.filterType === 'multiselect' ? [] : null;
        errors[col.field] = null;
      }
    });
    this.filterValues.set(filters);
    this.filterErrors.set(errors);

    // Reset flag so initial filters don't re-apply after a manual clear
    this.initialFiltersApplied = true;

    // Emit event
    this.onClearFilters.emit();
  }

  setFilterValue(field: string, value: any): void {
    this.filterValues.update((state) => ({
      ...state,
      [field]: value,
    }));
  }

  applyFilter(value: any, field: string, matchMode: string, table: any): void {
    this.setFilterValue(field, value);

    if (!this.serverSideFilter) {
      table.filter(value, field, matchMode);
    }
    this.onFilterChange.emit({ value, field, matchMode });
  }

  onTextFilterInput(value: string, field: string, table: any): void {
    const trimmedValue = (value ?? '').trim();
    const fieldLabel = this.getFieldLabel(field);

    if (!trimmedValue) {
      this.setFilterError(field, null);
      this.applyFilter('', field, 'contains', table);
      return;
    }

    if (/^[^a-zA-Z0-9À-ÿ]+$/.test(trimmedValue)) {
      this.setFilterError(field, {
        message: `Valore non valido per "${fieldLabel}".`,
        suggestion: `Inserisci almeno una lettera o un numero (es. ${this.getFieldExample(field)}).`,
      });
      return;
    }

    if (trimmedValue.length > 80) {
      this.setFilterError(field, {
        message: `Valore troppo lungo per il filtro "${fieldLabel}" (max 80 caratteri).`,
        suggestion: `Usa una parola chiave più breve (es. ${this.getFieldExample(field)}).`,
      });
      return;
    }

    this.setFilterError(field, null);
    this.applyFilter(value, field, 'contains', table);
  }

  onDateManualInput(event: any, field: string): void {
    // Intercetta l'input diretto dalla tastiera nel datepicker
    const inputValue = event?.target?.value || '';
    const fieldLabel = this.getFieldLabel(field);

    if (!inputValue.trim()) {
      this.setFilterError(field, null);
      return;
    }

    // Valida immediatamente il formato mentre l'utente digita
    const match = /^(\d{0,2})(\/)?(\d{0,2})(\/)?(\d{0,4})$/.exec(inputValue);
    
    if (!match) {
      // Input contiene caratteri non validi (es. "aaaaa", "###")
      this.setFilterError(field, {
        message: `Formato non valido per "${fieldLabel}".`,
        suggestion: 'Usa solo numeri e barre (es. 23/03/2026).',
      });
      return;
    }

    // Se l'input è parziale (es. "23/03"), non mostrare errore
    if (inputValue.length < 10) {
      this.setFilterError(field, null);
      return;
    }

    // Se l'input è completo (10 caratteri), valida il formato completo
    const completeMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(inputValue);
    if (!completeMatch) {
      this.setFilterError(field, {
        message: `Formato non valido per "${fieldLabel}".`,
        suggestion: 'Inserisci una data nel formato gg/mm/aaaa (es. 23/03/2026).',
      });
      return;
    }

    // Valida che sia una data reale
    const day = Number(completeMatch[1]);
    const month = Number(completeMatch[2]);
    const year = Number(completeMatch[3]);
    const parsed = new Date(year, month - 1, day);

    const isRealDate =
      parsed.getFullYear() === year && 
      parsed.getMonth() === month - 1 && 
      parsed.getDate() === day;

    if (!isRealDate) {
      this.setFilterError(field, {
        message: `Data non valida per "${fieldLabel}".`,
        suggestion: 'Verifica giorno e mese (es. 31/02 non è una data valida).',
      });
      return;
    }

    this.setFilterError(field, null);
  }

  onDateTextInput(rawValue: unknown, field: string, table: any): void {
    if (rawValue instanceof Date && !isNaN(rawValue.getTime())) {
      this.setFilterError(field, null);
      this.applyFilter(rawValue.toLocaleDateString('it-IT'), field, 'contains', table);
      return;
    }

    const value = typeof rawValue === 'string' ? rawValue.trim() : '';
    const fieldLabel = this.getFieldLabel(field);

    if (!value) {
      this.setFilterError(field, null);
      this.applyFilter('', field, 'contains', table);
      return;
    }

    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (!match) {
      this.setFilterError(field, {
        message: `Formato non valido per "${fieldLabel}".`,
        suggestion: 'Inserisci una data nel formato gg/mm/aaaa (es. 23/03/2026).',
      });
      return;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);

    const isRealDate =
      parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;

    if (!isRealDate) {
      this.setFilterError(field, {
        message: `Data non valida per "${fieldLabel}".`,
        suggestion: 'Verifica giorno e mese (es. 31/02 non è una data valida).',
      });
      return;
    }

    this.setFilterError(field, null);
    this.applyFilter(value, field, 'contains', table);
  }

  onDateFilter(event: any, field: string, table: any): void {
    if (event) {
      const dateStr = new Date(event).toLocaleDateString('it-IT');
      this.setFilterError(field, null);
      this.applyFilter(dateStr, field, 'contains', table);
    }
  }

  onMultiSelectFilter(values: any[], field: string, table: any): void {
    const nonEmpty = Array.isArray(values) && values.length > 0 ? values : null;
    this.applyFilter(nonEmpty, field, 'in', table);
  }

  getFilterError(field: string): { message: string; suggestion: string } | null {
    return this.filterErrors()[field] || null;
  }

  hasFilterError(field: string): boolean {
    return !!this.getFilterError(field);
  }

  private setFilterError(
    field: string,
    error: { message: string; suggestion: string } | null,
  ): void {
    this.filterErrors.update((state) => ({
      ...state,
      [field]: error,
    }));
  }

  private getFieldLabel(field: string): string {
    return this.columns.find((col) => col.field === field)?.title || field;
  }

  private getFieldExample(field: string): string {
    const normalizedField = field.toLowerCase();

    if (normalizedField.includes('contract') || normalizedField.includes('id')) {
      return 'CFOR-2026-0001';
    }

    if (normalizedField.includes('name') || normalizedField.includes('supplier')) {
      return 'Servizi professionali';
    }

    if (normalizedField.includes('status')) {
      return 'ATTIVO';
    }

    return 'testo o codice identificativo';
  }

  // =====================
  // ACTIONS
  // =====================
  handleSelectClick(rowData: any): void {
    this.onSelectClick.emit(rowData);
  }

  handleDetailClick(rowData: any): void {
    this.onDetailClick.emit(rowData);
  }

  handleDuplicateClick(rowData: any): void {
    this.onDuplicateClick.emit(rowData);
  }

  handleEditClick(rowData: any): void {
    console.log('handleEditClick - emitting event with:', rowData);
    this.onEditClick.emit(rowData);
  }

  handleDeleteClick(rowData: any): void {
    console.log('handleDeleteClick - emitting event with:', rowData);
    this.onDeleteClick.emit(rowData);
  }

  handleDownloadClick(rowData: any): void {
    this.onDownloadClick.emit(rowData);
  }

  isRetiredStatus(status: string | null | undefined): boolean {
    return status === 'RETIRED';
  }

  canEditRow(rowData: any): boolean {
    if (!this.onEditClick.observed || this.isRetiredStatus(rowData?.status)) {
      return false;
    }

    return (
      this.ignoreStatusChecks ||
      (rowData.status === 'DRAFT' && rowData.status !== 'GENERATED')
    );
  }

  canDeleteRow(rowData: any): boolean {
    if (
      !this.onDeleteClick.observed ||
      this.isRetiredStatus(rowData?.status) ||
      (this.deleteExcludedStatuses.length > 0 && this.deleteExcludedStatuses.includes(rowData?.status))
    ) {
      return false;
    }

    return (
      this.ignoreStatusChecks ||
      (rowData.status !== 'ACTIVE' &&
        rowData.status !== 'CANCELED' &&
        (rowData.status === 'DRAFT' ||
          rowData.status === 'INACTIVE' ||
          rowData.status === 'GENERATED'))
    );
  }

  // =====================
  // STATUS HELPERS
  // =====================
  getStatusSeverity(status: string): 'success' | 'danger' | 'secondary' | 'warn' | 'info' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'secondary';
      case 'DRAFT':
      case 'GENERATED':
        return 'secondary';
      case 'RETIRED':
        return 'secondary';
      // Dashboard values
      case 'IN_LAVORAZIONE':
      case 'IN_RENEWAL':
      // API values
      case 'IN_PROGRESS':
        return 'warn';
      // Alert values
      case 'UNREAD':
        return 'danger';
      case 'RESOLVED':
        return 'success';
      // Dashboard values
      case 'IN_ATTESA_DI_RISPOSTA':
      // API values
      case 'AWAITING_RESPONSE':
      case 'SUSPENDED':
      case 'CEASED':
      case 'EXPIRED':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getStatusDisplay(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'ATTIVO';
      case 'INACTIVE':
        return 'INATTIVO';
      case 'DRAFT':
        return 'BOZZA';
      case 'GENERATED':
        return 'DA ATTIVARE';
      case 'RETIRED':
        return 'RITIRATO';
      // Dashboard values
      case 'IN_LAVORAZIONE':
      // API values  
      case 'IN_PROGRESS':
        return 'IN LAVORAZIONE';
      case 'IN_RENEWAL':
        return 'IN RINNOVO';
      // Alert values
      case 'UNREAD':
        return 'Non letto';
      case 'RESOLVED':
        return 'Risolto';
      // Dashboard values
      case 'IN_ATTESA_DI_RISPOSTA':
      // API values
      case 'AWAITING_RESPONSE':
        return 'IN ATTESA DI RISPOSTA';
      case 'SUSPENDED':
        return 'SOSPESO';
      case 'EXPIRED':
        return 'SCADUTO';
      case 'CEASED':
        return 'CESSATO';
      default:
        return status;
    }
  }

  getTagDisplay(value: string, field: string): string {
    if (field === 'type' || field === 'category' || field === 'professionalCategory') {
      return this.getCategoryDisplay(value);
    }
    // I codici SOL hanno un ciclo di vita proprio (label/severity dedicati): usano il
    // field 'solStatus' per non collidere con i generici ACTIVE/INACTIVE/SUSPENDED.
    if (field === 'solStatus') {
      return this.getSolStatusDisplay(value);
    }
    return this.getStatusDisplay(value);
  }

  getTagSeverity(
    value: string,
    field: string,
  ): 'success' | 'danger' | 'secondary' | 'warn' | 'info' {
    if (field === 'type' || field === 'category' || field === 'professionalCategory') {
      return this.getCategorySeverity(value);
    }
    if (field === 'solStatus') {
      return this.getSolStatusSeverity(value);
    }
    return this.getStatusSeverity(value);
  }

  // ── Codici SOL (evolutiva MFA): stato con label/severity dedicati ────────────
  /** Etichetta leggibile dello stato del ciclo di vita del codice SOL. */
  private getSolStatusDisplay(status: string): string {
    switch (status) {
      case 'TO_ACTIVATE':
        return 'Da attivare';
      case 'ACTIVE':
        return 'Attivo';
      case 'SUSPENDED':
        return 'Sospeso';
      case 'INACTIVE':
        return 'Disattivo';
      default:
        return status;
    }
  }

  /** Severity del tag di stato del codice SOL. */
  private getSolStatusSeverity(
    status: string,
  ): 'success' | 'danger' | 'secondary' | 'warn' | 'info' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'SUSPENDED':
        return 'warn';
      case 'INACTIVE':
        return 'danger';
      case 'TO_ACTIVATE':
      default:
        return 'secondary';
    }
  }

  private getCategoryDisplay(category: string): string {
    const normalized = (category || '').toUpperCase().trim();

    switch (normalized) {
      case 'RE_EXPERT':
        return 'PERITO RE';
      case 'AUTO_EXPERT':
        return 'PERITO AUTO';
      case 'INVESTIGATOR':
        return 'INVESTIGATORE';
      case 'DOCTOR':
        return 'MEDICO';
      case 'LAWYER':
        return 'LEGALE';
      case 'BODYSHOP':
        return 'CARROZZERIA';
      case 'NK_BODYSHOP':
        return 'CARR. NETWORK';
      case 'HAIL_PROVIDER':
        return 'PROV. GRANDINE';
      case 'NETWORK':
        return 'NETWORK';
      default:
        return category;
    }
  }

  /**
   * Gets category tag severity based on category type
   */
  getCategorySeverity(category: string): 'success' | 'danger' | 'secondary' | 'warn' | 'info' {
    switch (category) {
      case 'AUTO_EXPERT':
        return 'info';
      case 'RE_EXPERT':
        return 'info';
      case 'INVESTIGATOR':
        return 'warn';
      case 'DOCTOR':
        return 'success';
      case 'LAWYER':
        return 'secondary';
      case 'BODYSHOP':
        return 'info';
      case 'NK_BODYSHOP':
        return 'info';
      case 'HAIL_PROVIDER':
        return 'info';
      case 'NETWORK':
        return 'secondary';
      default:
        return 'secondary';
    }
  }
}
